import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Building2, Copy, Eye, EyeOff, Globe, Pencil, Plus, RefreshCw, UserCog } from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { MasterShell } from "@/components/master-shell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useCompanies, useCreateCompany, useUpdateCompany } from "@/hooks/queries/platform";
import { PlatformApiError } from "@/lib/api/gpsdrive-platform-client";
import {
  gpsDriveCompaniesService,
  type Company,
  type CompanyDomainStatus,
} from "@/services/gpsdrive-companies.service";

export const Route = createFileRoute("/master/")({
  component: () => (
    <MasterShell>
      <CompaniesPage />
    </MasterShell>
  ),
  head: () => ({ meta: [{ title: "Empresas — Super Master — GPS Drive" }] }),
});

const dateFormatter = new Intl.DateTimeFormat("pt-BR", { dateStyle: "short" });
const currencyFormatter = new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" });

function statusBadge(status: Company["status"]) {
  if (status === "active") {
    return <Badge className="border-transparent bg-emerald-500 text-white hover:bg-emerald-500">Ativa</Badge>;
  }
  return <Badge className="border-transparent bg-red-500 text-white hover:bg-red-500">Suspensa</Badge>;
}

function CompaniesPage() {
  const { data: companies, isLoading } = useCompanies();

  return (
    <div className="mx-auto max-w-5xl space-y-6 p-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Empresas</h1>
          <p className="text-sm text-muted-foreground">
            Cadastro, domínio próprio e status (ativa/suspensa) de cada empresa da plataforma.
          </p>
        </div>
        <CompanyFormDialog
          trigger={
            <Button>
              <Plus className="h-4 w-4" />
              Nova empresa
            </Button>
          }
        />
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Carregando…</p>
      ) : !companies || companies.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center gap-2 py-10 text-center">
            <Building2 className="h-8 w-8 text-muted-foreground" />
            <p className="text-sm font-medium">Nenhuma empresa cadastrada ainda</p>
            <p className="text-xs text-muted-foreground">
              Clique em "Nova empresa" para cadastrar a primeira.
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Empresa</TableHead>
                <TableHead>Domínio</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Veículos</TableHead>
                <TableHead className="text-right">Valor/veículo</TableHead>
                <TableHead className="text-right">A cobrar</TableHead>
                <TableHead>Criada em</TableHead>
                <TableHead className="w-0" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {companies.map((company) => (
                <TableRow key={company.id}>
                  <TableCell className="font-medium">{company.name}</TableCell>
                  <TableCell className="text-muted-foreground">{company.domain ?? "—"}</TableCell>
                  <TableCell>{statusBadge(company.status)}</TableCell>
                  <TableCell className="text-right">{company.vehicleCount}</TableCell>
                  <TableCell className="text-right text-muted-foreground">
                    {company.pricePerVehicle !== null ? currencyFormatter.format(company.pricePerVehicle) : "—"}
                  </TableCell>
                  <TableCell className="text-right font-medium">
                    {company.pricePerVehicle !== null
                      ? currencyFormatter.format(company.pricePerVehicle * company.vehicleCount)
                      : "—"}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {dateFormatter.format(new Date(company.createdAt))}
                  </TableCell>
                  <TableCell className="space-x-2 text-right">
                    <CompanyDomainDialog company={company} />
                    <CompanyAdminsDialog company={company} />
                    <CompanyFormDialog
                      company={company}
                      trigger={
                        <Button variant="outline" size="sm">
                          <Pencil className="h-3.5 w-3.5" />
                          Editar
                        </Button>
                      }
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
}

// Domínio próprio da empresa (fase 5, 2026-10-17): mostra o registro de DNS
// que o Super Master cria no provedor do domínio, confere se já aponta pra
// cá e ativa (nginx + certificado, pelo agente do servidor). O domínio em si
// é cadastrado em Editar.
const DOMAIN_STATUS: Record<CompanyDomainStatus, { label: string; className: string }> = {
  sem_dominio: { label: "Sem domínio", className: "bg-muted text-muted-foreground" },
  aguardando_dns: { label: "Aguardando DNS", className: "bg-amber-500/15 text-amber-700 dark:text-amber-400" },
  dns_ok: { label: "DNS ok — falta ativar", className: "bg-sky-500/15 text-sky-700 dark:text-sky-400" },
  ativando: { label: "Ativando…", className: "bg-sky-500/15 text-sky-700 dark:text-sky-400" },
  ativo: { label: "Ativo", className: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400" },
  erro: { label: "Erro na ativação", className: "bg-destructive/15 text-destructive" },
};

function CopyValue({ value }: { value: string }) {
  return (
    <button
      type="button"
      onClick={() => {
        void navigator.clipboard?.writeText(value);
        toast.success("Copiado");
      }}
      className="inline-flex items-center gap-1.5 rounded border border-border bg-muted/50 px-2 py-1 font-mono text-sm hover:bg-muted"
    >
      {value}
      <Copy className="h-3 w-3 text-muted-foreground" />
    </button>
  );
}

function CompanyDomainDialog({ company }: { company: Company }) {
  const [open, setOpen] = useState(false);
  const qc = useQueryClient();
  const key = ["platform", "company-domain", company.id];
  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: key,
    queryFn: () => gpsDriveCompaniesService.getDomain(company.id),
    enabled: open,
    // Enquanto o agente ativa (certificado leva até ~1 min), acompanha sozinho.
    refetchInterval: (q) => (q.state.data?.status === "ativando" ? 4000 : false),
  });
  const [activating, setActivating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) setError(null);
  }, [open]);

  const handleActivate = async () => {
    setError(null);
    setActivating(true);
    try {
      const next = await gpsDriveCompaniesService.activateDomain(company.id);
      qc.setQueryData(key, next);
    } catch (err) {
      setError(err instanceof PlatformApiError ? err.message : "Não foi possível ativar o domínio.");
    } finally {
      setActivating(false);
    }
  };

  const status = data ? DOMAIN_STATUS[data.status] : null;
  const canActivate = data && (data.status === "dns_ok" || data.status === "erro" || data.status === "ativo");

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          <Globe className="h-3.5 w-3.5" />
          Domínio
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Domínio — {company.name}</DialogTitle>
          <DialogDescription>
            Endereço próprio onde os clientes e a equipe desta empresa entram, com a marca dela na tela de login.
          </DialogDescription>
        </DialogHeader>

        {isLoading || !data ? (
          <p className="text-sm text-muted-foreground">Conferindo o DNS…</p>
        ) : !data.domain ? (
          <p className="rounded-md border border-dashed border-border p-3 text-sm text-muted-foreground">
            Esta empresa ainda não tem domínio. Cadastre em <strong>Editar → Domínio próprio</strong> (ex.:
            cv.gpsdrive.com.br ou rastreamento.suaempresa.com.br) e volte aqui.
          </p>
        ) : (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span className="font-mono text-sm font-medium">{data.domain}</span>
              {status && <Badge className={status.className}>{status.label}</Badge>}
            </div>

            {data.record && (
              <div className="space-y-2 rounded-md border border-border p-3">
                <p className="text-sm font-medium">1. Crie este registro no DNS de {data.record.zone}</p>
                <p className="text-xs text-muted-foreground">
                  No painel onde o domínio está registrado (Registro.br, Cloudflare, Hostinger, GoDaddy…), em
                  “Zona DNS” / “Editar zona”.
                </p>
                <div className="grid grid-cols-[auto_1fr] items-center gap-x-4 gap-y-2 text-sm">
                  <span className="text-muted-foreground">Tipo</span>
                  <span className="font-mono">A</span>
                  <span className="text-muted-foreground">Nome / Host</span>
                  <span>
                    <CopyValue value={data.record.name} />
                    {data.record.name === "@" && (
                      <span className="ml-2 text-xs text-muted-foreground">(o próprio domínio, sem nada antes)</span>
                    )}
                  </span>
                  <span className="text-muted-foreground">Valor / Aponta para</span>
                  <span>
                    <CopyValue value={data.record.value} />
                  </span>
                  <span className="text-muted-foreground">TTL</span>
                  <span className="font-mono">{data.record.ttl} (ou o menor permitido)</span>
                </div>
                <p className="text-xs text-muted-foreground">
                  Se já existir um registro A, AAAA ou CNAME com esse mesmo nome, apague antes. Na Cloudflare, deixe a
                  nuvem <strong>cinza</strong> (“Somente DNS”).
                </p>
              </div>
            )}

            <div className="space-y-2 rounded-md border border-border p-3">
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm font-medium">2. Verificar o DNS</p>
                <Button variant="ghost" size="sm" onClick={() => void refetch()} disabled={isFetching}>
                  <RefreshCw className={`h-3.5 w-3.5 ${isFetching ? "animate-spin" : ""}`} />
                  Verificar agora
                </Button>
              </div>
              {data.dns && (
                <>
                  <p className={`text-sm ${data.dns.ok ? "text-emerald-700 dark:text-emerald-400" : "text-amber-700 dark:text-amber-400"}`}>
                    {data.dns.message}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Hoje aponta para: {data.dns.resolved.length ? data.dns.resolved.join(", ") : "nada"} · conferido às{" "}
                    {new Date(data.dns.checkedAt).toLocaleTimeString("pt-BR")}
                  </p>
                </>
              )}
            </div>

            <div className="space-y-2 rounded-md border border-border p-3">
              <p className="text-sm font-medium">3. Ativar</p>
              <p className="text-xs text-muted-foreground">
                Configura o servidor e emite o certificado de segurança (cadeado). Leva cerca de 1 minuto.
              </p>
              {data.url && (
                <p className="text-sm">
                  No ar:{" "}
                  <a href={data.url} target="_blank" rel="noreferrer" className="font-medium text-primary underline">
                    {data.url}
                  </a>
                </p>
              )}
              {data.lastJob?.output && (data.status === "erro" || data.status === "ativando") && (
                <pre className="max-h-40 overflow-auto whitespace-pre-wrap rounded bg-muted p-2 text-xs">
                  {data.lastJob.output}
                </pre>
              )}
              {error && <p className="text-sm text-destructive">{error}</p>}
            </div>
          </div>
        )}

        <DialogFooter>
          {data?.domain && (
            <Button onClick={handleActivate} disabled={!canActivate || activating || data.status === "ativando"}>
              {data.status === "ativando" || activating
                ? "Ativando…"
                : data.status === "ativo"
                  ? "Reativar"
                  : "Ativar domínio"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Administradores da empresa (pedido 2026-10-16: "criei a empresa mas não
// consigo criar o login do primeiro admin"). Lista + criar; o admin entra
// pelo login normal e cria os usuários/funcionários da empresa dele.
function CompanyAdminsDialog({ company }: { company: Company }) {
  const [open, setOpen] = useState(false);
  const qc = useQueryClient();
  const key = ["platform", "company-admins", company.id];
  const { data: admins, isLoading } = useQuery({
    queryKey: key,
    queryFn: () => gpsDriveCompaniesService.listAdmins(company.id),
    enabled: open,
  });
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [phone, setPhone] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setName("");
    setEmail("");
    setPassword("");
    setPhone("");
    setError(null);
  }, [open]);

  const strong = password.length >= 8 && /[A-Z]/.test(password) && /\d/.test(password) && /[^A-Za-z0-9]/.test(password);

  const handleCreate = async () => {
    setError(null);
    if (!name.trim() || !email.trim()) return setError("Informe nome e login.");
    if (!strong) return setError("Senha fraca: mínimo 8, com letra maiúscula, número e caractere especial.");
    setSaving(true);
    try {
      await gpsDriveCompaniesService.createAdmin(company.id, {
        name: name.trim(),
        email: email.trim(),
        password,
        phone: phone.trim() || undefined,
      });
      await qc.invalidateQueries({ queryKey: key });
      setName("");
      setEmail("");
      setPassword("");
      setPhone("");
    } catch (err) {
      setError(err instanceof PlatformApiError ? err.message : "Não foi possível criar o administrador.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          <UserCog className="h-3.5 w-3.5" />
          Administradores
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Administradores — {company.name}</DialogTitle>
          <DialogDescription>
            O administrador entra pelo login normal{company.domain ? ` (${company.domain})` : ""} e cria os usuários e
            funcionários da empresa. Ele só enxerga os dados desta empresa.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          {isLoading ? (
            <p className="text-sm text-muted-foreground">Carregando…</p>
          ) : !admins || admins.length === 0 ? (
            <p className="rounded-md border border-dashed border-border p-3 text-sm text-muted-foreground">
              Nenhum administrador ainda.
            </p>
          ) : (
            admins.map((a) => (
              <div key={a.id} className="flex items-center justify-between rounded-md border border-border px-3 py-2 text-sm">
                <div className="min-w-0">
                  <div className="truncate font-medium">{a.name}</div>
                  <div className="truncate text-xs text-muted-foreground">{a.email}</div>
                </div>
                <span className="shrink-0 text-xs text-muted-foreground">
                  {dateFormatter.format(new Date(a.createdAt))}
                </span>
              </div>
            ))
          )}
        </div>

        <div className="space-y-3 rounded-md border border-border p-3">
          <p className="text-sm font-medium">Novo administrador</p>
          <div className="space-y-1.5">
            <Label htmlFor="admin-name">Nome</Label>
            <Input id="admin-name" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="admin-email">Login (e-mail)</Label>
            <Input id="admin-email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="off" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="admin-password">Senha</Label>
            <div className="relative">
              <Input
                id="admin-password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
                className="pr-9"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground"
                aria-label={showPassword ? "Esconder senha" : "Mostrar senha"}
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            <p className="text-xs text-muted-foreground">Mínimo 8, com letra maiúscula, número e caractere especial.</p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="admin-phone">Celular</Label>
            <Input id="admin-phone" value={phone} onChange={(e) => setPhone(e.target.value)} inputMode="tel" />
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>

        <DialogFooter>
          <Button onClick={handleCreate} disabled={saving}>
            {saving ? "Criando…" : "Criar administrador"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CompanyFormDialog({ company, trigger }: { company?: Company; trigger: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [domain, setDomain] = useState("");
  const [pricePerVehicle, setPricePerVehicle] = useState("");
  // Agenda/Câmera (pedido 2026-09-16) — permissão de módulo ativada pelo
  // Super Master aqui mesmo, na criação ou edição da empresa. Default de
  // uma empresa nova segue o mesmo default do backend (ver
  // DEFAULT_MODULE_ENABLED): Agenda já vem ligada, Câmera precisa ser
  // ativada explicitamente.
  const [agendaEnabled, setAgendaEnabled] = useState(true);
  const [cameraEnabled, setCameraEnabled] = useState(false);
  const [frotaEnabled, setFrotaEnabled] = useState(false);
  const [bloqueioEnabled, setBloqueioEnabled] = useState(true);
  const createCompany = useCreateCompany();
  const updateCompany = useUpdateCompany();
  const isEditing = Boolean(company);
  const isPending = createCompany.isPending || updateCompany.isPending;

  useEffect(() => {
    if (!open) return;
    setName(company?.name ?? "");
    setDomain(company?.domain ?? "");
    setPricePerVehicle(company?.pricePerVehicle != null ? String(company.pricePerVehicle) : "");
    setAgendaEnabled(company?.modules.agenda ?? true);
    setCameraEnabled(company?.modules.camera ?? false);
    setFrotaEnabled(company?.modules.frota ?? false);
    setBloqueioEnabled(company?.modules.bloqueio ?? true);
  }, [open, company]);

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error("Informe o nome da empresa");
      return;
    }
    const trimmedPrice = pricePerVehicle.trim();
    let parsedPrice: number | null = null;
    if (trimmedPrice) {
      parsedPrice = Number(trimmedPrice.replace(",", "."));
      if (!Number.isFinite(parsedPrice) || parsedPrice < 0) {
        toast.error("Valor por veículo inválido");
        return;
      }
    }
    const modules = { agenda: agendaEnabled, camera: cameraEnabled, frota: frotaEnabled, bloqueio: bloqueioEnabled };
    try {
      if (company) {
        const input = { name: name.trim(), domain: domain.trim() || null, pricePerVehicle: parsedPrice, modules };
        await updateCompany.mutateAsync({ id: company.id, input });
        toast.success("Empresa atualizada");
      } else {
        const input = { name: name.trim(), domain: domain.trim() || null, modules };
        await createCompany.mutateAsync(input);
        toast.success("Empresa criada");
      }
      setOpen(false);
    } catch (err) {
      toast.error(err instanceof PlatformApiError ? err.message : "Falha ao salvar a empresa");
    }
  };

  const handleToggleStatus = async () => {
    if (!company) return;
    const nextStatus = company.status === "active" ? "suspended" : "active";
    try {
      await updateCompany.mutateAsync({ id: company.id, input: { status: nextStatus } });
      toast.success(nextStatus === "active" ? "Empresa reativada" : "Empresa suspensa");
      setOpen(false);
    } catch (err) {
      toast.error(err instanceof PlatformApiError ? err.message : "Falha ao alterar o status");
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{isEditing ? "Editar empresa" : "Nova empresa"}</DialogTitle>
          <DialogDescription>
            {isEditing
              ? "Altere o nome ou o domínio próprio desta empresa."
              : "Cadastre o nome e, se já souber, o domínio próprio desta empresa."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="company-name">Nome</Label>
            <Input
              id="company-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ex: WP Rastreamento"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="company-domain">Domínio próprio</Label>
            <Input
              id="company-domain"
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              placeholder="Ex: rastreamento.suaempresa.com.br"
            />
          </div>
          {isEditing ? (
            <div className="space-y-2">
              <Label htmlFor="company-price">Valor por veículo (mensal)</Label>
              <Input
                id="company-price"
                inputMode="decimal"
                value={pricePerVehicle}
                onChange={(e) => setPricePerVehicle(e.target.value)}
                placeholder="Ex: 19,90"
              />
              <p className="text-xs text-muted-foreground">
                Quanto esta empresa paga à GPS Drive por veículo rastreado ({company?.vehicleCount ?? 0}{" "}
                {company?.vehicleCount === 1 ? "veículo" : "veículos"} hoje).
              </p>
            </div>
          ) : null}
          <div className="space-y-3 rounded-md border border-border p-3">
            <p className="text-sm font-medium">Módulos liberados</p>
            <div className="flex items-center justify-between gap-4">
              <div>
                <Label htmlFor="company-module-agenda">Agenda</Label>
                <p className="text-xs text-muted-foreground">Menu Agenda (técnicos e agendamentos).</p>
              </div>
              <Switch id="company-module-agenda" checked={agendaEnabled} onCheckedChange={setAgendaEnabled} />
            </div>
            <div className="flex items-center justify-between gap-4">
              <div>
                <Label htmlFor="company-module-camera">Câmera</Label>
                <p className="text-xs text-muted-foreground">
                  Menu Câmera (visível só para admin/funcionário — nunca para o cliente final).
                </p>
              </div>
              <Switch id="company-module-camera" checked={cameraEnabled} onCheckedChange={setCameraEnabled} />
            </div>
            <div className="flex items-center justify-between gap-4">
              <div>
                <Label htmlFor="company-module-frota">Gestão de Frota</Label>
                <p className="text-xs text-muted-foreground">
                  Painel, motoristas, custos, manutenção e locação. Admin sempre vê; funcionário e cliente frotista
                  só com a permissão "Gestão de Frota".
                </p>
              </div>
              <Switch id="company-module-frota" checked={frotaEnabled} onCheckedChange={setFrotaEnabled} />
            </div>
            <div className="flex items-center justify-between gap-4">
              <div>
                <Label htmlFor="company-module-bloqueio">Bloqueio e desbloqueio</Label>
                <p className="text-xs text-muted-foreground">
                  Desligado, ninguém da empresa bloqueia ou desbloqueia veículos: web, app, link compartilhado e regras
                  automáticas.
                </p>
              </div>
              <Switch id="company-module-bloqueio" checked={bloqueioEnabled} onCheckedChange={setBloqueioEnabled} />
            </div>
          </div>
        </div>

        <DialogFooter className="sm:justify-between">
          {isEditing ? (
            <Button
              type="button"
              variant="outline"
              onClick={handleToggleStatus}
              disabled={isPending}
              className={company?.status === "active" ? "text-red-600 hover:text-red-600" : "text-emerald-600 hover:text-emerald-600"}
            >
              {company?.status === "active" ? "Suspender" : "Reativar"}
            </Button>
          ) : (
            <span />
          )}
          <Button onClick={handleSave} disabled={isPending}>
            {isPending ? "Salvando…" : "Salvar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
